import { List, Map, Set } from 'immutable';
import {
  types as jobTypes,
  records as jobRecords,
} from '@saucelabs/sl-module-jobs';
import {
  types as buildsTypes,
  records as buildsRecords,
} from '@saucelabs/sl-module-builds';
import { constants as cloudTypeConstants } from '@saucelabs/sl-module-cloudtype';
import { statusToEvent, transition, Event, State } from '@saucelabs/ds-job-card';
import { addTreeViewUuids } from '@saucelabs/sl-component-viewtree-inspector';

import * as constants from './constants';
import * as types from './types';
import { updateTestDetailsForJob } from './operations';
import { buildTypes as buildsVDCTypes } from './../build/vdc/types';
import { buildTypes as buildsRDCTypes } from './../build/rdc/types';
import {
  functions as webdriverCommandFunctions,
  types as webdriverCommandTypes,
} from './../../webdriver-commands';
import {
  types as commandTypes,
} from './../batch-framework-commands';

/**
 * @typedef {import("./types").AssetMap} AssetMap
 */

const initialState = Map({
  /**
   * @type AssetMap
   */
  assets: {},
  assetListFetched: false,
  build: new buildsRecords.BuildRecord(),
  commands: List(),
  commandsLoadStatus: constants.COMMANDS_FETCH_LOADING,
  commandsState: Map({
    active: -1,
    expanded: Set(),
  }),
  /**
   * runningState is state of the ui for queued/running jobs
   *
   * It's used to manage certain ui elements as a job transitions between states,
   * e.g. running -> stopped
   * @type State
   */
  runningState: State.INIT,
  job: new jobRecords.JobRecord(),
  screenshotIndex: 0,
  selectorsMapping: {},
  testfairySdkData: null,
});

const mergeAssets = ({ state, payload }) => {
  const assets = state.get('assets');
  const withValues = Object.entries(payload).reduce((acc, [key, value]) => {
    if ([null, undefined].includes(value)) {
      return acc;
    }

    return { ...acc, [key]: value };
  }, {});
  return state.set('assets', { ...assets, ...withValues });
};

const jobReducer = (state = initialState, { type, payload, meta }) => {
  switch (type) {
    case jobTypes.JOB_GET.SUCCESS: {
      const job = new jobRecords.JobRecord(payload);
      const event = statusToEvent(job.getStatus());
      const runningState = transition(state.get('runningState'), event);

      return state.withMutations((mutable) => {
        mutable.set('job', job);
        if (runningState) {
          mutable.set('runningState', runningState);
        }
      });
    }
    case jobTypes.UP_JOB_GET.SUCCESS: {
      const job = new jobRecords.RdcJobRecord(payload);
      const event = statusToEvent(job.getStatus());
      const runningState = transition(state.get('runningState'), event);

      return state.withMutations((mutable) => {
        mutable.set('job', job);
        if (runningState) {
          mutable.set('runningState', runningState);
        }
      });
    }
    case commandTypes.PARSE_ESPRESSO_JOB:
    case commandTypes.PARSE_NATIVE_FRAMEWORK_TEST_CASES:
    case commandTypes.PARSE_XCUITEST_JOB:
      return state.withMutations((mutable) => {
        mutable.set('commands', payload);
        mutable.set('commandsLoadStatus', constants.COMMANDS_FETCH_SUCCESS);
      });
    case webdriverCommandTypes.PARSE_APPIUM_JOB:
    case webdriverCommandTypes.PARSE_SELENIUM_JOB: {
      const withoutVisuals = webdriverCommandFunctions.updateVisualCommands(payload);
      return state
        .set('selectorsMapping', webdriverCommandFunctions.buildSeleniumSelectorsMap(withoutVisuals))
        .set('commands', withoutVisuals)
        .set('commandsLoadStatus', constants.COMMANDS_FETCH_SUCCESS);
    }
    case types.PARSE_CYPRESS_JOB:
    case types.PARSE_PLAYWRIGHT_JOB:
    case types.PARSE_TESTCAFE_JOB:
    case types.PARSE_PUPPETER_JOB:
    case types.PARSE_CUCUMBER_JOB:
    case types.SHOW_TEST_REPORT:
    case commandTypes.PARSE_JUNIT:
      return state.withMutations((mutable) => {
        mutable.set('commands', payload);
        mutable.set('commandsLoadStatus', constants.COMMANDS_FETCH_SUCCESS);
      });
    case types.PARSE_TESTFAIRY_LOGS: {
      return state.set('testfairySdkData', payload.session?.viewtree ? addTreeViewUuids(payload) : payload);
    }
    case types.PARSE_CRASH_LOGS: {
      return state.set('crashLogs', payload);
    }
    case buildsTypes.BUILD_GET_BY_NAME.SUCCESS:
      return state.set('build', new buildsRecords.BuildRecord(payload));
    case buildsVDCTypes.GET_BUILD_BY_JOB_ID.SUCCESS:
      return state.set('build', new buildsRecords.BuildRecord(payload));
    case buildsRDCTypes.GET_BUILD_BY_JOB_ID.SUCCESS:
      return state.set('build', new buildsRecords.BuildRecord({
        ...payload,
        cloudType: cloudTypeConstants.CLOUD_REAL,
      }));
    case jobTypes.CHANGE_VISIBILITY.SUCCESS:
      return state.set('job', new jobRecords.JobRecord({
        ...state.get('job').toJS(),
        ...payload,
      }));
    case jobTypes.ASSET_LIST_GET.SUCCESS: {
      return mergeAssets({ state, payload }).set('assetListFetched', true);
    }
    case jobTypes.SET_ASSET_LIST: {
      return mergeAssets({ state, payload });
    }
    case jobTypes.JOB_STOP.REQUEST:
    case jobTypes.JOB_STOP_RDC.REQUEST: {
      const currentState = state.get('runningState');
      const newState = transition(currentState, Event.STOP);
      return state.set('runningState', newState);
    }
    case jobTypes.JOB_STOP.FAILURE:
    case jobTypes.JOB_STOP_RDC.FAILURE: {
      const currentState = state.get('runningState');
      const newState = transition(currentState, Event.RESUME);
      return state.set('runningState', newState);
    }
    case jobTypes.JOB_BREAKPOINT.REQUEST: {
      const currentState = state.get('runningState');
      const newState = transition(currentState, Event.PAUSE);
      return state.set('runningState', newState);
    }
    case jobTypes.JOB_BREAKPOINT.SUCCESS: {
      const currentState = state.get('runningState');
      const newState = transition(currentState, Event.PAUSE);
      return state.set('runningState', newState);
    }
    case jobTypes.JOB_BREAKPOINT.FAILURE: {
      const currentState = state.get('runningState');
      const newState = transition(currentState, Event.RESUME);
      return state.set('runningState', newState);
    }
    case types.COMMAND_ICON_SELECT: {
      const { id } = payload;
      let expanded = state.getIn(['commandsState', 'expanded']);
      if (expanded.has(id)) {
        expanded = expanded.remove(id);
      } else {
        expanded = expanded.add(id);
      }

      return state.setIn(['commandsState', 'expanded'], expanded);
    }
    case types.UPDATE_ACTIVE_COMMAND: {
      const { collapseCurrent, newActiveId, oldActiveId } = payload;
      const find = (command) => command?.id === newActiveId;

      return state.withMutations((newState) => {
        if (newState.get('commands').findIndex(find) !== -1) {
          newState.setIn(['commandsState', 'active'], newActiveId);
        }
        if (collapseCurrent) {
          let expanded = newState.getIn(['commandsState', 'expanded']);
          expanded = expanded.remove(oldActiveId);
          newState.setIn(['commandsState', 'expanded'], expanded);
        }
      });
    }
    case types.NO_TEST_CASES_AVAILABLE:
    case types.NO_COMMANDS_AVAILABLE:
    case types.ASSETS_EXPIRED:
      return state.set('commandsLoadStatus', constants.COMMANDS_FETCH_FAILURE);
    case jobTypes.ASSET_GET.FAILURE: {
      const { config } = payload;
      if (config && config.url && /\/log\.json$/.test(config.url)) {
        return state.set('commandsLoadStatus', constants.COMMANDS_FETCH_FAILURE);
      }
      return state;
    }

    case updateTestDetailsForJob.pending.type: {
      return state.withMutations((mutable) => {
        mutable
          .setIn(['job', 'name'], meta.arg.testDetails.name)
          .setIn(['job', 'passed'], meta.arg.testDetails.passed);

        // consolidated_status is calculated in the backend.
        // This simplified logic is used to show the proper status label
        // before the backend response arrives.
        let newConsolidatedStatus;
        if (meta.arg.testDetails.passed === true) {
          newConsolidatedStatus = 'passed';
        } else if (meta.arg.testDetails.passed === false) {
          newConsolidatedStatus = 'failed';
        } else {
          newConsolidatedStatus = mutable.getIn(['job', 'consolidated_status']);
        }
        mutable.setIn(['job', 'consolidated_status'], newConsolidatedStatus);
      });
    }
    case updateTestDetailsForJob.fulfilled.type: {
      return state.withMutations((mutable) => {
        mutable
          .setIn(['job', 'name'], payload?.name ?? state.getIn(['job', 'name']))
          .setIn(['job', 'passed'], payload?.passed ?? state.getIn(['job', 'passed']))
          .setIn(['job', 'consolidated_status'], payload?.consolidated_status ?? state.getIn(['job', 'consolidated_status']));
      });
    }
    case updateTestDetailsForJob.rejected.type:
      return state
        .set('job', meta.arg.job);

    case types.CLEANUP:
      return initialState;

    case types.SELECT_SCREENSHOT:
      return state.set('screenshotIndex', payload);

    default:
      return state;
  }
};

export default jobReducer;
export { initialState };
